import { useNavigate } from 'react-router-dom';
import './TopicCard.css';
import { useDispatch, useSelector } from 'react-redux';
import { usePatents } from '../../../hooks/usePatents';
import { useBooks } from '../../../hooks/useBooks';
import { useLibrary } from '../../../hooks/useLibrary';
import {
  setLoadingMetasearch,
  setWord,
  setWordFetched,
  setWordFetchedAdvanced,
} from '../../../app/features/metasearchSlice';
import { sendUserInformation } from '../../../services/sendUserInformation';
import {
  resetMultimedia,
  setIsMediaDataFetched,
  setIsMultimediaLoading,
  setMultimedia,
  setTotalMultimedia,
} from '../../../app/features/multimediaSlice';
import { getMultimedia } from '../../../services/getMultimedia';
import {
  setIsNewsDataFetched,
  setIsNewsLoading,
  setNews,
  setQueryNewsView,
  setResetNewsQuery,
  setTotalNewsQuery,
} from '../../../app/features/newsSlice';
import { getNewsFromQuery } from '../../../services/getNewsFromQuery';
import {
  resetDictionaty,
  setElementaryData,
  setIsDictionaryDataFetched,
  setIsElementaryDataLoading,
  setIsMedicalDataLoading,
  setIsThesaurusDataLoading,
  setMedicalData,
  setThesaurusData,
} from '../../../app/features/dictionarySlice';
import {
  getElementaryDictionary,
  getMedicalDictionary,
  getThesaurusDictionary,
} from '../../../services/getDictionary';
import { currentYear } from '../../../utils/getYearsFilters';
import { setActiveLoginModal } from '../../../app/features/userSlice';

const TopicCard = ({
  iconSrc,
  title,
  onButtonClick = () => {},
  canExecuteLogger,
  hasLoginSettingsActive,
  name,
  isDark,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Hooks
  const { getPatentsDataSocket } = usePatents();
  const { getBooksDataSocket } = useBooks();
  const { getLibraryDataSocket } = useLibrary();

  // Slices
  const { user_email } = useSelector((state) => state.user);
  const { searchType, allLibrarySources } = useSelector(
    (state) => state.library
  );
  const { allBooksSources } = useSelector((state) => state.books);
  const { allPatentsSources } = useSelector((state) => state.patents);
  const { isMedicalInstitution, institutionCode } = useSelector(
    (state) => state.institution
  );

  const getData = (name) => {
    dispatch(setWord(name.toLowerCase()));
    // Envio de datos de busqueda y de usuario
    sendUserInformation(
      'library',
      name.toLowerCase(),
      user_email,
      institutionCode
    );
    // Activa loading del Metabuscador
    dispatch(setLoadingMetasearch(true));
    searchType !== 'simple'
      ? dispatch(setWordFetchedAdvanced(name.toLowerCase()))
      : dispatch(setWordFetched(name.toLowerCase()));

    // Library
    getLibraryDataSocket(
      name.toLowerCase(),
      1,
      false,
      1990,
      currentYear,
      allLibrarySources,
      [],
      searchType,
      false
    );

    // Multimedia
    dispatch(resetMultimedia());
    dispatch(setIsMultimediaLoading(true));
    getMultimedia(name.toLowerCase(), 1)
      .then((response) => {
        if (response.status === 200) {
          dispatch(setIsMediaDataFetched(true));
          dispatch(setMultimedia(response?.data?.data));
          dispatch(setTotalMultimedia(response?.data?.count));
        }
        dispatch(setIsMultimediaLoading(false));
      })
      .catch((error) => {
        console.error('Request error:', error);
        dispatch(setMultimedia([]));
        dispatch(setTotalMultimedia(0));
        dispatch(setIsMultimediaLoading(false));
      });

    // News
    dispatch(setResetNewsQuery());
    dispatch(setQueryNewsView(true));
    dispatch(setIsNewsLoading(true));
    getNewsFromQuery(name.toLowerCase(), 1)
      .then((response) => {
        if (response.status === 200) {
          dispatch(setIsNewsDataFetched(true));
          dispatch(setNews(response?.data?.data));
          dispatch(setTotalNewsQuery(response?.data?.count));
        }
        dispatch(setIsNewsLoading(false));
      })
      .catch((error) => {
        console.error(error);
        dispatch(setNews([]));
        dispatch(setTotalNewsQuery(0));
        dispatch(setIsNewsLoading(false));
      });

    // Dictionary
    dispatch(resetDictionaty());
    if (isMedicalInstitution) {
      dispatch(setIsMedicalDataLoading(true));
      getMedicalDictionary(name.toLowerCase())
        .then((response) => {
          if (typeof response.data[0] !== 'string') {
            let dataMedical = [];
            response.data?.map((element) => {
              dataMedical.push({
                meta: element.meta,
                shortdef: element.shortdef,
              });
              return null;
            });
            dispatch(setMedicalData(dataMedical));
          } else {
            dispatch(setMedicalData(response?.data));
          }
          dispatch(setIsMedicalDataLoading(false));
          dispatch(setIsDictionaryDataFetched(true));
        })
        .catch((error) => {
          dispatch(setIsMedicalDataLoading(false));
          console.error('Request error:', error);
        });
    }
    dispatch(setIsThesaurusDataLoading(true));
    getThesaurusDictionary(name.toLowerCase())
      .then((response) => {
        if (typeof response.data[0] !== 'string') {
          let dataThesaurus = [];
          response.data?.map((element) => {
            dataThesaurus.push({
              meta: element.meta,
              shortdef: element.shortdef,
            });
            return null;
          });
          dispatch(setThesaurusData(dataThesaurus));
        } else {
          dispatch(setThesaurusData(response?.data));
        }
        dispatch(setIsThesaurusDataLoading(false));
        dispatch(setIsDictionaryDataFetched(true));
      })
      .catch((error) => {
        console.error('Request error:', error);
        dispatch(setIsThesaurusDataLoading(false));
      });
    dispatch(setIsElementaryDataLoading(true));
    getElementaryDictionary(name.toLowerCase())
      .then((response) => {
        if (typeof response.data[0] !== 'string') {
          let dataElementary = [];
          response.data?.map((element) => {
            dataElementary.push({
              meta: element.meta,
              shortdef: element.shortdef,
              history: element.history,
            });
            return null;
          });
          dispatch(setElementaryData(dataElementary));
        } else {
          dispatch(setElementaryData(response?.data));
        }
        dispatch(setIsElementaryDataLoading(false));
        dispatch(setIsDictionaryDataFetched(true));
      })
      .catch((error) => {
        console.error('Request error:', error);
        dispatch(setIsElementaryDataLoading(false));
      });

    // Patents
    getPatentsDataSocket(
      name.toLowerCase(),
      1,
      1990,
      currentYear,
      allPatentsSources
    );

    // Books
    getBooksDataSocket(
      name.toLowerCase(),
      1,
      1990,
      currentYear,
      allBooksSources,
      [],
      '',
      '',
      'en'
    );

    // Redireccion hacia Articles
    navigate(`/articles`);
  };

  const handleClick = () => {
    if (!hasLoginSettingsActive) {
      getData(name);
      return;
    }

    return canExecuteLogger && hasLoginSettingsActive
      ? getData(name)
      : dispatch(setActiveLoginModal(true));
  };

  return (
    <button className={`topic-card`} onClick={handleClick}>
      <div className='topic-card__icon'>
        <img src={iconSrc} alt={title + ' icon'} />
      </div>
      <p className={`topic-card__title`}>{title}</p>
    </button>
  );
};

export default TopicCard;
